// ---------------------------------------------------------------------------
// The four states every data screen needs: loading, empty, error, and a
// permission refusal.
//
// Having them in one file means a table that is still loading looks the same
// everywhere in the application, and no screen ever renders a blank rectangle
// with no explanation of what is happening.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';
import { AlertTriangle, Inbox, Lock, RefreshCw, WifiOff } from 'lucide-react';
import clsx from 'clsx';
import { ApiError } from '../../lib/apiClient';
import { useI18n } from '../../i18n/I18nProvider';

// --- loading ---------------------------------------------------------------

export function SkeletonRows({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div className={clsx('space-y-2.5', className)} role="status" aria-busy="true">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="skeleton h-11 w-full" />
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

export function SkeletonCards({ count = 4 }: { count?: number }) {
  return (
    <div
      className="grid grid-cols-2 gap-4 lg:grid-cols-4"
      role="status"
      aria-busy="true"
    >
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="skeleton h-24 w-full rounded-card" />
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

/// Shown while a lazy-loaded screen is being fetched.
///
/// Deliberately quiet: a skeleton the shape of a page, not a spinner in the
/// middle of nothing. On a shop's network the wait is usually a few hundred
/// milliseconds and only happens once per screen, so anything more emphatic
/// would draw attention to something that is about to stop mattering.
export function PageLoading() {
  return (
    <div className="mx-auto max-w-6xl space-y-5" role="status" aria-busy="true">
      <div className="space-y-2">
        <div className="skeleton h-6 w-48" />
        <div className="skeleton h-4 w-72" />
      </div>
      <div className="skeleton h-64 w-full rounded-card" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

// --- empty -----------------------------------------------------------------

export function EmptyState({
  title,
  description,
  icon,
  action,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-surface-sunken text-ink-muted">
        {icon ?? <Inbox className="h-6 w-6" aria-hidden="true" />}
      </div>
      <p className="text-sm font-semibold text-ink">{title}</p>
      {description ? <p className="mt-1 max-w-md text-sm text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

// --- error -----------------------------------------------------------------

/// Turns any thrown value into a message a shop employee can act on.
/// Stack traces and server internals are never shown.
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useI18n();

  const isNetwork = error instanceof ApiError && error.code === 'NETWORK_ERROR';
  const isForbidden = error instanceof ApiError && error.code === 'FORBIDDEN';

  const title = isNetwork
    ? t.errors.networkTitle
    : isForbidden
      ? t.errors.forbiddenTitle
      : t.errors.genericTitle;

  const body = isNetwork
    ? t.errors.networkBody
    : isForbidden
      ? t.errors.forbiddenBody
      : error instanceof ApiError
        ? error.message
        : t.errors.unexpectedBody;

  const Icon = isNetwork ? WifiOff : isForbidden ? Lock : AlertTriangle;

  return (
    <div
      className="flex flex-col items-center justify-center px-6 py-12 text-center"
      role="alert"
      aria-live="polite"
    >
      <div
        className={clsx(
          'mb-3 flex h-12 w-12 items-center justify-center rounded-full',
          isForbidden ? 'bg-surface-sunken text-ink-muted' : 'bg-burgundy-red/10 text-burgundy-red',
        )}
      >
        <Icon className="h-6 w-6" aria-hidden="true" />
      </div>
      <p className="text-sm font-semibold text-ink">{title}</p>
      <p className="mt-1 max-w-md text-sm text-ink-muted">{body}</p>

      {/* Retrying a permission refusal would only produce the same refusal,
          so the button is offered only when trying again could actually work. */}
      {onRetry && !isForbidden ? (
        <button type="button" onClick={onRetry} className="btn-secondary mt-4">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {t.common.retry}
        </button>
      ) : null}
    </div>
  );
}
